/**
 * User-facing copy for typed rejection codes — the honest-messaging surface
 * (test-spec §6.6.2 default 11: refused commands get a visible, dismissible
 * message; PO decision 15: `seat_reconnecting` and `seat_taken` must read
 * differently because they are different truths).
 *
 * Codes are the wire contract (shared/join, shared/commands); this module is
 * the ONLY place their user-facing text lives, so the two situations §5.1
 * conflated can never collapse back into one string. Distinct code → distinct
 * copy is asserted in the unit tests.
 */

import { COMMAND_REJECTION, JOIN_REJECTION } from "@asarimitsuha/table-core";

/** Any code the server may hand us; unknown strings fall back gracefully. */
export type RejectionCode = string;

/**
 * Join-flow rejections (thrown as the join promise's reason, or surfaced by
 * the seat panel). `seat_reconnecting` deliberately tells the truth the GM
 * and the would-be joiner both need: the seat's owner may be coming back.
 */
const JOIN_COPY: Record<string, string> = {
  [JOIN_REJECTION.invalidJoin]:
    "This table link isn’t valid. Check the room code and, for a GM, the key.",
  [JOIN_REJECTION.seatTaken]:
    "That seat is already connected in another window.",
  [JOIN_REJECTION.seatReconnecting]:
    "That player dropped and may be reconnecting — their seat is held for a moment. Try again shortly, or pick another seat.",
  [JOIN_REJECTION.seatRemoved]:
    "The GM removed that seat from the table. It can’t be reclaimed — you can rejoin as a new player.",
  [JOIN_REJECTION.invalidName]:
    "Please enter a name of 1–24 characters.",
  [JOIN_REJECTION.protocolMismatch]:
    "This client build is out of date with the table server. Reload the page for the newest version — if that doesn’t fix it, tell your GM.",
};

/**
 * Command rejections. Every code the shared validator can emit has copy here;
 * the fallback exists only for a code added server-side before this map, never
 * as the normal path.
 */
const COMMAND_COPY: Record<string, string> = {
  [COMMAND_REJECTION.malformed]: "That action couldn’t be understood.",
  [COMMAND_REJECTION.notSeated]: "You’re not seated at this table yet.",
  [COMMAND_REJECTION.unknownToken]: "That token no longer exists.",
  [COMMAND_REJECTION.notOwner]: "You can only change tokens you own.",
  [COMMAND_REJECTION.gmOnly]: "Only the GM can do that.",
  [COMMAND_REJECTION.outOfBounds]: "That’s off the edge of the map.",
  [COMMAND_REJECTION.invalidTokenSize]:
    "Tokens are 1×1 in this version.",
  [COMMAND_REJECTION.invalidName]: "Please enter a valid name.",
  [COMMAND_REJECTION.unknownSeat]: "That seat no longer exists.",
  [COMMAND_REJECTION.unknownStat]: "That stat can’t be changed.",
  /* ── M3 timeline/effect codes ── */
  [COMMAND_REJECTION.unknownEffect]: "That effect no longer exists.",
  [COMMAND_REJECTION.notEffectController]:
    "Only the GM, whoever applied this effect, or the target’s owner can remove it.",
  [COMMAND_REJECTION.timelineNotBegun]: "Combat hasn’t started yet.",
  [COMMAND_REJECTION.timelineAlreadyBegun]: "Combat is already in progress.",
  [COMMAND_REJECTION.currentActorLocked]:
    "The current actor can’t be moved in the order mid-turn.",
  [COMMAND_REJECTION.alreadyActedLocked]:
    "That token already acted this round — it can’t move ahead of the current turn.",
  [COMMAND_REJECTION.untilRemovedLocked]:
    "That effect lasts until removed — its duration can’t be changed. Remove it instead.",
  [COMMAND_REJECTION.duplicateTimelineEntry]:
    "That token is already in the initiative order.",
  [COMMAND_REJECTION.unknownTimelineEntry]:
    "That initiative entry no longer exists.",
};

const GENERIC = "That action was refused.";

/**
 * Map a typed rejection code to a sentence a player can act on. Join and
 * command tables are merged; an unknown code returns the generic fallback so
 * the UI never shows a raw wire token to a human.
 */
export function rejectionMessage(code: RejectionCode): string {
  return JOIN_COPY[code] ?? COMMAND_COPY[code] ?? GENERIC;
}

/** True when the code is one this module has explicit copy for (vs. fallback).
 *  Lets callers log an unmapped code without changing what the user sees. */
export function hasRejectionCopy(code: RejectionCode): boolean {
  return code in JOIN_COPY || code in COMMAND_COPY;
}
